import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface Family {
  id: number;
  combined_name: string;
  status: string;
}

interface Student {
  id: number;
  first_name: string;
  family_name: string;
  home_country: string;
  semester_id?: number;
}

interface FamilyAvailabilityData {
  id?: number;
  family_id: number;
  semester_id: number;
  max_students: number;
  preferences_notes: string;
}


interface FamilyAvailabilityProps {
  selectedSemesterId: number | null;
  selectedSeason: string;
  selectedYear: number;
  onAvailabilityChange: () => void;
}

export const FamilyAvailability: React.FC<FamilyAvailabilityProps> = ({ 
  selectedSemesterId,
  selectedSeason,
  selectedYear,
  onAvailabilityChange 
}) => {
  const [families, setFamilies] = useState<Family[]>([]);
  const [availabilities, setAvailabilities] = useState<FamilyAvailabilityData[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFamily, setEditingFamily] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ max_students: 1, preferences_notes: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadAvailabilityData = useCallback(async () => {
    if (!selectedSemesterId) return;
    
    try {
      const availabilitiesResult = await supabase
        .from('family_availability')
        .select('*')
        .eq('semester_id', selectedSemesterId);
      
      if (availabilitiesResult.data) setAvailabilities(availabilitiesResult.data);
    } catch (error) {
      console.error('Error loading availability data:', error);
    }
  }, [selectedSemesterId]);

  useEffect(() => {
    if (selectedSemesterId) {
      loadAvailabilityData();
    }
  }, [selectedSemesterId, loadAvailabilityData]);


  const loadData = async () => {
    setLoading(true);
    try {
      const [familiesResult, assignmentsResult, studentsResult] = await Promise.all([
        supabase.from('families').select('*').eq('status', 'Active').order('combined_name'),
        supabase.from('assignments').select('*'),
        supabase.from('students').select('id, first_name, family_name, home_country, semester_id')
      ]);

      if (familiesResult.data) setFamilies(familiesResult.data);
      if (assignmentsResult.data) setAssignments(assignmentsResult.data);
      if (studentsResult.data) setStudents(studentsResult.data);

    } catch (error) {
      console.error('Error loading availability data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFamilyAvailability = (familyId: number) => {
    return availabilities.find(a => a.family_id === familyId);
  };

  const getAssignedStudents = (familyId: number) => {
    return assignments.filter(a => a.family_id === familyId && a.semester_id === selectedSemesterId);
  };

  const getStudentById = (studentId: number) => {
    return students.find(s => s.id === studentId);
  };

  const getCurrentSemester = () => {
    return { name: `${selectedSeason} ${selectedYear}` };
  };

  const handleEditFamily = (family: Family) => {
    const availability = getFamilyAvailability(family.id);
    setEditingFamily(family.id);
    setEditForm({
      max_students: availability?.max_students || 1,
      preferences_notes: availability?.preferences_notes || ''
    });
  };

  const handleSaveAvailability = async () => {
    if (!editingFamily || !selectedSemesterId) return;

    try {
      const existingAvailability = getFamilyAvailability(editingFamily);
      
      if (existingAvailability) {
        // Update existing
        await supabase
          .from('family_availability')
          .update({
            max_students: editForm.max_students,
            preferences_notes: editForm.preferences_notes,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingAvailability.id);
      } else {
        // Create new
        await supabase
          .from('family_availability')
          .insert({
            family_id: editingFamily,
            semester_id: selectedSemesterId,
            max_students: editForm.max_students,
            preferences_notes: editForm.preferences_notes
          });
      }

      setEditingFamily(null);
      await loadData();
      onAvailabilityChange();
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('Error saving availability');
    }
  };

  const handleDeleteAvailability = async (familyId: number) => {
    if (!window.confirm('Remove this family\'s availability for this semester?')) return;

    try {
      const availability = getFamilyAvailability(familyId);
      if (availability?.id) {
        await supabase
          .from('family_availability')
          .delete()
          .eq('id', availability.id);
        
        await loadData();
        onAvailabilityChange();
      }
    } catch (error) {
      console.error('Error deleting availability:', error);
    }
  };

  if (loading) return <div>Loading family availability...</div>;

  const currentSemester = getCurrentSemester();
  const totalSlots = availabilities.reduce((sum, a) => sum + a.max_students, 0);
  const totalAssignments = availabilities.reduce((sum, a) => sum + getAssignedStudents(a.family_id).length, 0);

  // Sort families: those with availability first (alphabetically), then others (alphabetically)
  const sortedFamilies = [...families].sort((a, b) => {
    const aHasAvailability = getFamilyAvailability(a.id) !== undefined;
    const bHasAvailability = getFamilyAvailability(b.id) !== undefined;
    
    // If one has availability and the other doesn't, prioritize the one with availability
    if (aHasAvailability && !bHasAvailability) return -1;
    if (!aHasAvailability && bHasAvailability) return 1;
    
    // If both have same availability status, sort alphabetically by name
    return a.combined_name.localeCompare(b.combined_name);
  });

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
        <h3>Family Availability - {currentSemester?.name}</h3>
        
        <div style={{ color: '#ccc', fontSize: '0.9em' }}>
          {totalSlots} total slots • 
          {totalAssignments} assigned • 
          {totalSlots - totalAssignments} available
        </div>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
        gap: '15px' 
      }}>
        {sortedFamilies.map(family => {
          const availability = getFamilyAvailability(family.id);
          const assignedStudents = getAssignedStudents(family.id);
          const isEditing = editingFamily === family.id;
          const hasAssignments = assignedStudents.length > 0;
          
          return (
            <div 
              key={family.id}
              style={{
                padding: '15px',
                border: hasAssignments ? '2px solid #ffc107' : 
                        availability ? '2px solid #28a745' : '1px solid #666',
                borderRadius: '8px',
                backgroundColor: hasAssignments ? '#3a3a1e' :
                               availability ? '#1e3a1e' : '#2a2a2a',
                color: 'white'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                <span>{family.combined_name}</span>
                <div style={{ fontSize: '0.8em' }}>
                  {!isEditing && (
                    <>
                      <button
                        onClick={() => handleEditFamily(family)}
                        style={{ 
                          marginRight: '5px', 
                          padding: '2px 8px', 
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        {availability ? 'Edit' : 'Add'}
                      </button>
                      {availability && (
                        <button
                          onClick={() => handleDeleteAvailability(family.id)}
                          style={{ 
                            padding: '2px 8px', 
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px' }}>
                      Max Students:
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={editForm.max_students}
                        onChange={(e) => setEditForm({ ...editForm, max_students: parseInt(e.target.value) || 0 })}
                        style={{
                          marginLeft: '10px',
                          padding: '5px',
                          backgroundColor: '#333',
                          color: 'white',
                          border: '1px solid #666',
                          borderRadius: '3px',
                          width: '60px'
                        }}
                      />
                    </label>
                  </div>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px' }}>
                      Preferences & Notes:
                      <textarea
                        value={editForm.preferences_notes}
                        onChange={(e) => setEditForm({ ...editForm, preferences_notes: e.target.value })}
                        placeholder="Dietary restrictions, gender preferences, special requests, etc."
                        rows={3}
                        style={{
                          width: '100%',
                          marginTop: '5px',
                          padding: '8px',
                          backgroundColor: '#333',
                          color: 'white',
                          border: '1px solid #666',
                          borderRadius: '3px',
                          resize: 'vertical'
                        }}
                      />
                    </label>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleSaveAvailability}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingFamily(null)}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {availability ? (
                    <div>
                      <div style={{ marginBottom: '8px', fontSize: '1.1em' }}>
                        <strong>Hosting: {availability.max_students} student{availability.max_students !== 1 ? 's' : ''}</strong>
                      </div>
                      {availability.preferences_notes && (
                        <div style={{ fontSize: '0.9em', color: '#ccc', fontStyle: 'italic' }}>
                          "{availability.preferences_notes}"
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: '#999', fontStyle: 'italic' }}>
                      Not available this semester
                    </div>
                  )}
                  
                  {hasAssignments && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ 
                        padding: '8px', 
                        backgroundColor: '#ffc107', 
                        borderRadius: '4px',
                        color: '#000',
                        marginBottom: '5px',
                        fontSize: '0.9em',
                        fontWeight: 'bold'
                      }}>
                        📚 Currently hosting {assignedStudents.length} student{assignedStudents.length > 1 ? 's' : ''}:
                      </div>
                      {assignedStudents.map((assignment, index) => {
                        const student = getStudentById(assignment.student_id);
                        return (
                          <div 
                            key={assignment.id}
                            style={{ 
                              padding: '8px', 
                              backgroundColor: '#3a3a1e', 
                              borderRadius: '3px',
                              marginBottom: '3px',
                              fontSize: '0.9em'
                            }}
                          >
                            {student ? (
                              <div style={{ fontWeight: 'bold', color: '#fff' }}>
                                {student.first_name} {student.family_name}
                                {student.home_country && (
                                  <span style={{ color: '#ccc', fontSize: '0.85em', fontWeight: 'normal' }}>
                                    {' '}({student.home_country})
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div style={{ color: '#999', fontStyle: 'italic' }}>
                                Student #{assignment.student_id} (not found)
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};